import { useEffect, useId, useRef, useState } from 'react';

import { cx } from '../../../shared/lib/cx.js';
import { todayInputValue } from '../../../shared/lib/dates.js';
import {
  addMonths,
  fullDateLabel,
  monthGrid,
  monthLabel,
  monthOf,
  previousDay,
  WEEKDAY_LABELS,
  type YearMonth,
} from '../model/calendar.js';
import { dayLabelOf } from '../model/timelineGroups.js';
import styles from './QuickAddControls.module.css';

/**
 * The transaction date: defaults to today, and opens a calendar when tapped.
 *
 * The calendar is drawn by this app, not by the browser. Two native attempts failed on the
 * PO's device (2026-08-18/19): the popup opened and closed again before a day could be picked,
 * because something outside React kept rewriting the input's `type` attribute and the browser
 * rebuilds the control — picker included — when that happens.
 *
 * It opens as a MODAL, not as a panel under the button (PO, 2026-08-19). Rendered inline, the
 * calendar fell below the fold on a phone: the user had to scroll the form to see it, and a
 * finger dragging across the grid landed on a day — the app recorded a date the user never
 * chose. A modal shows the whole month at once with nothing to scroll, so the only way to pick
 * a day is to mean it.
 *
 * `<dialog>` + `showModal()` rather than a hand-built overlay: the browser puts it in the top
 * layer (no stacking-context surprises), traps focus, and closes on Escape by itself. Baseline
 * widely available since March 2022 — developer.mozilla.org/en-US/docs/Web/API/
 * HTMLDialogElement/showModal, checked 2026-08-19.
 */
export function DatePicker({
  value,
  onChange,
  timeZone,
}: {
  value: string;
  onChange: (date: string) => void;
  timeZone: string;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);

  const today = todayInputValue(timeZone);
  const [isOpen, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState<YearMonth>(() => monthOf(value));

  // Focus starts on the day already selected, not on the month arrows: a keyboard or screen
  // reader user lands on "where I am", and the focus ring does not point at the wrong control.
  useEffect(() => {
    if (isOpen) selectedDayRef.current?.focus();
  }, [isOpen]);

  // The page behind a modal must not scroll: on iOS it otherwise slides under the dialog.
  useEffect(() => {
    if (!isOpen) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  function open() {
    // Dismiss the on-screen keyboard first, or it covers the calendar on a short screen.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setVisibleMonth(monthOf(value));
    setOpen(true);
    dialogRef.current?.showModal();
  }

  function close() {
    dialogRef.current?.close();
  }

  function choose(dayKey: string) {
    onChange(dayKey);
    close();
  }

  return (
    <section className={styles.section}>
      <p className={styles.sectionLabel}>Ngày giao dịch</p>

      <button
        type="button"
        className={styles.dateTrigger}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={open}
      >
        <span className={styles.dateIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" focusable="false">
            <g
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
              <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
            </g>
          </svg>
        </span>
        <span className={styles.dateValue}>{dayLabelOf(value, today)}</span>
        <span className={styles.dateHint} aria-hidden="true">
          Đổi
        </span>
      </button>

      <dialog
        ref={dialogRef}
        className={styles.calendarDialog}
        aria-labelledby={titleId}
        // Escape and the close button both come through here, so the trigger's state and the
        // dialog can never disagree.
        onClose={() => setOpen(false)}
        onClick={(event) => {
          // A click on the backdrop reports the dialog itself as the target.
          if (event.target === dialogRef.current) close();
        }}
      >
        <div className={styles.calendar}>
          <div className={styles.calendarHeader}>
            <button
              type="button"
              className={styles.calendarNav}
              aria-label="Tháng trước"
              onClick={() => setVisibleMonth((month) => addMonths(month, -1))}
            >
              ‹
            </button>
            <span className={styles.calendarMonth} id={titleId} aria-live="polite">
              {monthLabel(visibleMonth)}
            </span>
            <button
              type="button"
              className={styles.calendarNav}
              aria-label="Tháng sau"
              onClick={() => setVisibleMonth((month) => addMonths(month, 1))}
            >
              ›
            </button>
          </div>

          <div className={styles.weekdays} aria-hidden="true">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div
            className={styles.days}
            role="group"
            aria-label={`Ngày trong ${monthLabel(visibleMonth)}`}
          >
            {monthGrid(visibleMonth).map((cell) => (
              <button
                key={cell.dayKey}
                type="button"
                ref={cell.dayKey === value ? selectedDayRef : undefined}
                className={cx(
                  styles.day,
                  !cell.inMonth && styles.dayOutside,
                  cell.dayKey === today && styles.dayToday,
                  cell.dayKey === value && styles.daySelected,
                )}
                aria-label={fullDateLabel(cell.dayKey)}
                aria-pressed={cell.dayKey === value}
                onClick={() => choose(cell.dayKey)}
              >
                {cell.day}
              </button>
            ))}
          </div>

          {/* The two days almost every entry belongs to, one tap away. */}
          <div className={styles.calendarShortcuts}>
            <button type="button" className={styles.shortcut} onClick={() => choose(today)}>
              Hôm nay
            </button>
            <button
              type="button"
              className={styles.shortcut}
              onClick={() => choose(previousDay(today))}
            >
              Hôm qua
            </button>
          </div>

          {/* Leaving without choosing must be as easy as choosing — and must change nothing. */}
          <button type="button" className={styles.calendarClose} onClick={close}>
            Đóng
          </button>
        </div>
      </dialog>
    </section>
  );
}
